import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ConstraintChip, FeedbackBanner, HintLadder, Panel, Tag } from '@usacow/ds';
import { DrillHost, type DrillKind } from '@usacow/drills';
import { api, type Scenario } from '../lib/api';

const SEED_ID = 'silver_static_range_queries_014';

interface Step {
  id: string;
  label: string;
  drill?: DrillKind;
}

const STEPS: Step[] = [
  { id: 'read', label: 'Read' },
  { id: 'deconstruct', label: 'Deconstruct', drill: 'deconstructor' },
  { id: 'constraints', label: 'Constraints', drill: 'constraint_sniffer' },
  { id: 'candidates', label: 'Candidates', drill: 'pattern_discriminator' },
  { id: 'predict', label: 'Predict', drill: 'state_visualizer' },
  { id: 'invariant', label: 'Invariant', drill: 'invariant_builder' },
  { id: 'bridge', label: 'Bridge', drill: 'implementation_bridge' },
  { id: 'code', label: 'Code' },
];

interface StepResult {
  status: 'correct' | 'partially_correct' | 'incorrect';
  score: number;
  repair: string | null;
}

export function GuidedProblem() {
  const navigate = useNavigate();
  const [bundle, setBundle] = useState<Scenario | null>(null);
  const [version, setVersion] = useState('1');
  const [missing, setMissing] = useState(false);
  const [step, setStep] = useState(0);
  const [results, setResults] = useState<Record<string, StepResult>>({});
  const [hints, setHints] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.getScenario(SEED_ID);
        if (!cancelled) {
          setBundle(r.bundle);
          setVersion(r.version);
        }
      } catch {
        // Dev convenience: publish the seed, then retry once.
        if (import.meta.env.DEV) {
          await api.seedContent().catch(() => {});
          try {
            const r = await api.getScenario(SEED_ID);
            if (!cancelled) {
              setBundle(r.bundle);
              setVersion(r.version);
            }
            return;
          } catch {
            /* fall through */
          }
        }
        if (!cancelled) setMissing(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (missing) return <div className="route-loading">No published scenario yet.</div>;
  if (!bundle) return <div className="route-loading">Loading problem…</div>;

  const variant = bundle.problem_variants[0];
  const cur = STEPS[step]!;

  function recordStep(id: string, r: StepResult) {
    setResults((prev) => ({ ...prev, [id]: r }));
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  }

  async function finish() {
    const graded = Object.values(results);
    const score = graded.length
      ? Math.round(graded.reduce((a, r) => a + r.score, 0) / graded.length)
      : 1000;
    const firstFail = STEPS.find((s) => results[s.id]?.status === 'incorrect')?.label ?? null;
    await api
      .recordAttempt({
        scenarioId: bundle!.scenario_id,
        contentVersion: version,
        mode: 'guided',
        score,
        topic: bundle!.topic_families[0],
        skill: 'modeling',
        firstFailStage: firstFail,
      })
      .catch(() => {});
    navigate('/app');
  }

  if (done) {
    const graded = Object.entries(results);
    const wrong = graded.filter(([, r]) => r.status === 'incorrect');
    const repair = wrong.map(([, r]) => r.repair).find(Boolean) ?? null;
    return (
      <div className="scr scr--narrow">
        <div className="scr-head">
          <div>
            <div className="t-eyebrow">loop complete · {variant?.theme}</div>
            <h1 className="scr-h">Reflection</h1>
          </div>
        </div>
        <FeedbackBanner
          state={wrong.length === 0 ? 'correct' : 'warning'}
          title={wrong.length === 0 ? 'Clean run.' : `${wrong.length} step(s) to repair.`}
          slug={repair ? `repair: ${repair}` : undefined}
        >
          {wrong.length === 0
            ? 'You moved through modeling, constraints, and proof without a misstep.'
            : 'The steps you missed are where attempts at this level usually go wrong. They are scheduled for repair.'}
        </FeedbackBanner>
        <Panel title="Per-step result" style={{ marginTop: 14 }}>
          <div className="gp-steps">
            {STEPS.map((s) => {
              const r = results[s.id];
              const tone = !r
                ? 'neutral'
                : r.status === 'incorrect'
                  ? 'incorrect'
                  : r.status === 'partially_correct'
                    ? 'partial'
                    : 'correct';
              return (
                <div className="gp-steprow" key={s.id}>
                  <span>{s.label}</span>
                  <Badge tone={tone as never} variant="outline">
                    {r ? r.status.replace('_', ' ') : 'reflection'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Panel>
        <div className="gp-finish">
          <Button variant="primary" size="lg" onClick={finish}>
            Finish &amp; schedule repair
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="gp">
      <div className="gp-rail">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            className={`gp-rail__step${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`}
            onClick={() => i <= step && setStep(i)}
          >
            <span className="gp-rail__n">{i < step ? '✓' : String(i + 1).padStart(2, '0')}</span>
            <span className="gp-rail__l">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="gp-body">
        <Panel title="Statement" meta={bundle.division} style={{ flex: 'none', width: 360 }}>
          <p className="gp-stmt">{variant?.text}</p>
          <p className="gp-stmt__obj">
            <b>Objective:</b> {bundle.formal_model.objective}
          </p>
          <div className="gp-stmt__tags">
            {bundle.topic_families.map((t) => (
              <Tag key={t}>{t.replace(/_/g, ' ')}</Tag>
            ))}
          </div>
        </Panel>

        <div className="gp-work">
          {cur.id === 'read' && (
            <div className="gp-read">
              <h3 className="gp-step-h">Read the problem</h3>
              <p className="gp-step-p">
                First pass. Understand what&apos;s asked and what you&apos;re given — don&apos;t
                model yet.
              </p>
              <FeedbackBanner state="warning" title="Reflection (not graded)">
                State the objective in one line, then move on when you can.
              </FeedbackBanner>
              <Button variant="primary" style={{ marginTop: 14 }} onClick={() => setStep(step + 1)}>
                I&apos;ve got it
              </Button>
            </div>
          )}

          {cur.id === 'code' && (
            <div className="gp-read">
              <h3 className="gp-step-h">Code &amp; test</h3>
              <p className="gp-step-p">
                The one place full code is written in guided mode. (Sandbox judging arrives in the
                contest phase.)
              </p>
              <textarea className="gp-code" placeholder="// write your solution" rows={8} />
              <Button
                variant="primary"
                style={{ marginTop: 12 }}
                onClick={() => recordStep('code', { status: 'correct', score: 1000, repair: null })}
              >
                Submit
              </Button>
            </div>
          )}

          {cur.drill && (
            <DrillHost
              key={cur.id}
              kind={cur.drill}
              continueLabel="Next step"
              onComplete={({ grade, event }) =>
                recordStep(cur.id, {
                  status:
                    grade.status === 'skipped'
                      ? 'incorrect'
                      : (grade.status as StepResult['status']),
                  score: grade.score,
                  repair: event.repair_action,
                })
              }
            />
          )}
        </div>

        <div className="gp-inspect">
          <Panel title="Constraints" meta="extracted" style={{ marginBottom: 12 }}>
            <div className="gp-chips">
              {Object.entries(bundle.formal_model.constraints).map(([k, v]) => (
                <ConstraintChip key={k} label={k} value={String(v)} />
              ))}
              <ConstraintChip value={bundle.complexity_profile.expected} variant="derived" />
            </div>
          </Panel>
          <Panel title="Hint ladder" meta={`${hints} revealed`}>
            <HintLadder hints={bundle.hint_ladder} revealed={hints} onReveal={setHints} />
          </Panel>
        </div>
      </div>
    </div>
  );
}
